/**
 * wallet-core/crypto.ts
 *
 * AES-256-GCM encryption for private keys using the Web Crypto API.
 * Password → PBKDF2-SHA256 (250k iterations) → AES-GCM key.
 * Output format: "pw1.<base64(salt + iv + ciphertext)>"
 *
 * Security properties:
 *  - Private key never stored in plain text
 *  - Each encryption generates a fresh random salt + IV
 *  - AES-GCM provides authenticated encryption (tamper-evident)
 *  - PBKDF2 slows brute-force attacks on the password
 */

const PREFIX = 'pw1.'
const SALT_LENGTH = 16  // bytes
const IV_LENGTH = 12    // bytes (recommended for AES-GCM)

/** PBKDF2 iteration count for wallet key derivation (single source of truth). */
export const PBKDF2_ITERATIONS = 250_000

// ─── Helpers ─────────────────────────────────────────────────────────────────

function toBase64(bytes: Uint8Array): string {
  let binary = ''
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i])
  return btoa(binary)
}

function fromBase64(b64: string): Uint8Array {
  const binary = atob(b64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes
}

function concat(...arrays: Uint8Array[]): Uint8Array {
  const total = arrays.reduce((sum, a) => sum + a.length, 0)
  const result = new Uint8Array(total)
  let offset = 0
  for (const a of arrays) {
    result.set(a, offset)
    offset += a.length
  }
  return result
}

// ─── Key derivation ───────────────────────────────────────────────────────────

async function deriveKey(password: string, salt: Uint8Array): Promise<CryptoKey> {
  const enc = new TextEncoder()
  const baseKey = await crypto.subtle.importKey(
    'raw',
    enc.encode(password),
    'PBKDF2',
    false,
    ['deriveKey'],
  )
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt.buffer as ArrayBuffer,
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  )
}

// ─── Public API ───────────────────────────────────────────────────────────────

/** Random 32-byte secret as hex (64 chars); used as internal encryption password (never shown to user). */
export function generateInternalPasswordHex(): string {
  const bytes = new Uint8Array(32)
  crypto.getRandomValues(bytes)
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')
}

/**
 * Encrypt a WIF private key with the user's password.
 * Returns a portable string safe to store in IndexedDB / localStorage.
 */
export async function encryptPrivateKey(privateKey: string, password: string): Promise<string> {
  if (!privateKey?.trim()) {
    throw new Error('Private key cannot be empty')
  }
  if (!password || password.length === 0) {
    throw new Error('Password cannot be empty')
  }
  const enc = new TextEncoder()
  const salt = crypto.getRandomValues(new Uint8Array(SALT_LENGTH))
  const iv   = crypto.getRandomValues(new Uint8Array(IV_LENGTH))

  const key  = await deriveKey(password, salt)
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    enc.encode(privateKey),
  )

  const payload = concat(salt, iv, new Uint8Array(ciphertext))
  return PREFIX + toBase64(payload)
}

/**
 * Decrypt a private key that was encrypted by `encryptPrivateKey`.
 * Throws if the password is wrong or the data is tampered with.
 */
export async function decryptPrivateKey(encrypted: string, password: string): Promise<string> {
  if (!encrypted.startsWith(PREFIX)) {
    throw new Error('Invalid encrypted key format')
  }

  const payload = fromBase64(encrypted.slice(PREFIX.length))
  const salt = payload.slice(0, SALT_LENGTH)
  const iv   = payload.slice(SALT_LENGTH, SALT_LENGTH + IV_LENGTH)
  const ciphertext = payload.slice(SALT_LENGTH + IV_LENGTH)

  const key = await deriveKey(password, salt)

  let plaintext: ArrayBuffer
  try {
    plaintext = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      ciphertext,
    )
  } catch {
    throw new Error('Decryption failed — wrong password or corrupted data')
  }

  return new TextDecoder().decode(plaintext)
}

/**
 * Verify a password against an encrypted key without returning the plaintext.
 * Use this to validate the password before any signing operation.
 */
export async function verifyPassword(encrypted: string, password: string): Promise<boolean> {
  try {
    await decryptPrivateKey(encrypted, password)
    return true
  } catch {
    return false
  }
}

/**
 * SHA-256 hex digest of UTF-8(pin + accountName) for PIN verification (Web Crypto).
 */
export async function hashPin(pin: string, accountName: string): Promise<string> {
  const enc = new TextEncoder()
  const buf = await crypto.subtle.digest('SHA-256', enc.encode(pin + accountName))
  return Array.from(new Uint8Array(buf))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')
}

/** Password string for encryptPrivateKey when wrapping key for PIN unlock */
export function pinDerivedPassword(pin: string, accountName: string): string {
  return `${pin}|${accountName}`
}
